/**
 * Favourite careers — account-backed, cloud-hydrated.
 *
 * Pins live in `saved_careers`, keyed to the signed-in learner, so a pin made
 * on a phone is there on a laptop. The mirror used to sit in `localStorage`,
 * which meant a cleared browser silently changed what the learner saw; it now
 * goes through `cloudStorage`, which is an in-memory cache backed by the
 * learner's own database record. A visitor who pins before signing in keeps
 * their pins in that cache and they are pushed up on the first authenticated
 * read.
 */
import { supabase } from "@/integrations/supabase/client";
import { cloudStorage } from "@/lib/cloudStore";

const BUFFER_KEY = "wekit_saved_careers";

export interface SavedCareer {
  ciId: string;
  title: string;
  family: string | null;
  band?: string | null;
}

/** Buffered records were written by several screens over time, so the shape is
 *  read defensively rather than trusted. */
function readBuffer(): SavedCareer[] {
  try {
    const raw = cloudStorage.getItem(BUFFER_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(arr)) return [];
    return arr
      .map((r: Record<string, unknown>) => ({
        ciId: String(r.ciId ?? r.ci_id ?? r.id ?? ""),
        title: String(r.title ?? ""),
        family: (r.family as string) ?? null,
      }))
      .filter((r) => r.ciId && r.title);
  } catch {
    return [];
  }
}

function writeBuffer(rows: SavedCareer[]) {
  try {
    cloudStorage.setItem(BUFFER_KEY, JSON.stringify(rows));
  } catch {
    /* unserialisable value — the table copy is the source of truth anyway */
  }
}

export function localSavedCount(): number {
  return readBuffer().length;
}

/**
 * Read the learner's pins. Signed out, this is the in-memory buffer. Signed
 * in, the buffer is merged upward first so nothing pinned before sign-in is
 * lost, then the account list is returned.
 */
export async function fetchSavedCareers(userId: string | null): Promise<SavedCareer[]> {
  const buffered = readBuffer();
  if (!userId) return buffered;

  if (buffered.length) {
    await supabase.from("saved_careers").upsert(
      buffered.map((r) => ({
        user_id: userId,
        ci_id: r.ciId,
        title: r.title,
        family: r.family,
      })),
      { onConflict: "user_id,ci_id", ignoreDuplicates: true },
    );
  }

  const { data, error } = await supabase
    .from("saved_careers")
    .select("ci_id, title, family, band")
    .order("created_at", { ascending: false });

  if (error) return buffered;

  const rows: SavedCareer[] = (data ?? []).map((r) => ({
    ciId: r.ci_id,
    title: r.title,
    family: r.family,
    band: r.band,
  }));
  writeBuffer(rows);
  return rows;
}

export async function saveCareer(
  userId: string | null,
  career: SavedCareer,
): Promise<void> {
  const buffered = readBuffer();
  if (!buffered.some((r) => r.ciId === career.ciId)) writeBuffer([career, ...buffered]);
  if (!userId) return;
  const { error } = await supabase.from("saved_careers").upsert(
    {
      user_id: userId,
      ci_id: career.ciId,
      title: career.title,
      family: career.family,
      band: career.band ?? null,
    },
    { onConflict: "user_id,ci_id", ignoreDuplicates: true },
  );
  // The caller shows the heart optimistically, so a failed write has to be
  // loud enough for it to put the heart back.
  if (error) throw error;
}

export async function unsaveCareer(userId: string | null, ciId: string): Promise<void> {
  writeBuffer(readBuffer().filter((r) => r.ciId !== ciId));
  if (!userId) return;
  const { error } = await supabase
    .from("saved_careers")
    .delete()
    .eq("user_id", userId)
    .eq("ci_id", ciId);
  if (error) throw error;
}
